import { useState } from 'react';
import { api } from '../../api/client';
import { B } from '../../constants/brand';
import { Modal, ModalFooter, Btn, Select } from '../ui';
import { InlineSearchPicker, useDealPickerItems } from '../tasks/TaskComponents';

// SaveFileToDealModal — put a Harvi-drafted workbook or document on a deal's
// Documents tab, as an ordinary document.
//
// The server builds the same file the download builds and lands it through the
// same path an emailed attachment takes, so it is then read, filed into a
// folder and copied to SharePoint like any upload. The deal is pre-filled from
// the draft (or the thread it was drafted in) and can be changed; the server
// re-checks that the person can write there. Pressing Save twice for the same
// deal and format returns the first copy rather than making a second.

export const SAVE_TIMEOUT_MS = 90000;

const FORMAT_OPTIONS = {
  workbook: [{ value: 'xlsx', label: 'Excel (.xlsx)' }],
  document: [{ value: 'docx', label: 'Word (.docx)' }, { value: 'pdf', label: 'PDF' }],
};

/** "deal:<id>" / "detail:<id>" — the picker's value shape — from a target. */
export function comboFor(target) {
  if (target?.dealDetailId) return `detail:${target.dealDetailId}`;
  if (target?.dealId) return `deal:${target.dealId}`;
  return '';
}

/** The request body for a picker value and a format. */
export function saveBody(combo, format) {
  const [kind, id] = String(combo || '').split(':');
  if (!id) return null;
  return kind === 'detail' ? { format, dealDetailId: id } : { format, dealId: id };
}

export function SaveFileToDealModal({ draft, defaultTarget, onClose, onSaved }) {
  const payload = draft.payload || {};
  const formats = FORMAT_OPTIONS[draft.kind] || FORMAT_OPTIONS.document;
  const initial = comboFor(payload.dealId || payload.dealDetailId
    ? { dealId: payload.dealId, dealDetailId: payload.dealDetailId } : defaultTarget);
  const [combo, setCombo] = useState(initial);
  const [format, setFormat] = useState(formats[0].value);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const { dealPickerItems, dealPickerError } = useDealPickerItems();

  const submit = async () => {
    const body = saveBody(combo, format);
    if (!body) { setErr('Choose a deal or capital item.'); return; }
    setBusy(true); setErr(null);
    try {
      const res = await api.post(`assistant/drafts/${draft.draftId}/save-to-deal`, body,
        { timeout: SAVE_TIMEOUT_MS });
      onSaved?.(res);
      onClose?.();
    } catch (e) {
      // The API's refusal names the reason (no access, expired, no storage).
      setErr(e?.message || 'The file could not be saved.');
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Save to a deal's Documents" onClose={onClose} width={520}>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 12 }}>
        Adds <strong>{payload.title || 'this file'}</strong> to the deal as a document. It is then read,
        filed into a folder and copied to SharePoint, like any upload. It is saved as yours.
      </div>
      <InlineSearchPicker
        label="Deal or capital item"
        value={combo}
        onChange={setCombo}
        items={dealPickerItems}
        placeholder="Search deals & details…"
        clearLabel="— Choose a deal —"
      />
      {dealPickerError && <div style={{ fontSize: 12, color: B.red, marginTop: 6 }}>Could not load deals.</div>}
      {formats.length > 1 && (
        <div style={{ marginTop: 12 }}>
          <Select label="Format" value={format} onChange={setFormat} options={formats} />
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: B.red, marginTop: 10 }}>{err}</div>}
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn onClick={submit} loading={busy} disabled={busy || !combo}>Save to deal</Btn>
      </ModalFooter>
    </Modal>
  );
}

export default SaveFileToDealModal;
